import type { FC } from 'react';
import { useEffect, useRef } from 'react';

import { useTranslation } from '../../../api/useTranslation';
import { useAudioTrigger } from '../../../controllers/audioController/hooks';
import { useCoreDispatch } from '../../../store/CoreStore';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { headerActions } from '../../../store/slices/header/slice';
import { useHelpSlice } from '../../../store/slices/help/selectors';
import { helpActions } from '../../../store/slices/help/slice';
import { useIsLargerThanWide } from '../../../styles/hooks/media';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { Icon, Icons } from '../../icons/';
import { Loader } from '../../loader';
import type { HelpNavProps } from '../types';
import { HelpText } from '../utils/HelpText';

import styles from './HelpNav.module.css';

export const HelpNavConnected: FC<HelpNavProps> = ({ onNavigation, helpChapters, inNavDrawer, selectedHelpPage }) => {
  const helpNavRef = useRef<HTMLDivElement>(null);
  const helpFile = useHelpSlice.getHelpFile();
  const isSorted = useHelpSlice.getIsHelpSorted();
  const tableNameKey = useConfigSlice.tableConfig()?.tableName ?? '';
  const isSelected = !!selectedHelpPage;

  const dispatch = useCoreDispatch();
  const audioTrigger = useAudioTrigger();
  const isWideScreen = useIsLargerThanWide();
  const { translations } = useTranslation();
  const tableName = translations[tableNameKey] || tableNameKey;

  const handleNavigation = (chapter: string): void => {
    if (inNavDrawer) {
      dispatch(headerActions.pushNavRoute(chapter));
    } else {
      dispatch(headerActions.setSelectedHelpPage(chapter));
    }
    onNavigation();
  };

  useEffect(() => {
    if (!isSorted) {
      dispatch(helpActions.sortHelpChapters(helpChapters));
      dispatch(helpActions.setIsSorted(true));
    }
  }, [isSorted, dispatch, helpChapters]);

  useEffect(() => {
    if (helpFile) {
      const chapterNotSelected =
        helpChapters.length > 0 && (!selectedHelpPage || !helpChapters.includes(selectedHelpPage));

      // Select first chapter by default on wide screens
      if (chapterNotSelected && isWideScreen) {
        dispatch(headerActions.setSelectedHelpPage(helpChapters[0]));
      }
    }
  }, [helpChapters, helpFile, selectedHelpPage, isWideScreen, dispatch]);

  // Store height of help navigation element in redux to limit height of HelpModalConnected.
  useEffect(() => {
    const calculateHeight = (): void => {
      const helpNavHeight = helpNavRef.current?.getBoundingClientRect().height;

      if (helpNavHeight) {
        dispatch(helpActions.setHelpNavHeight(helpNavHeight));
      }
    };

    if (helpFile) {
      calculateHeight();
    }
  }, [dispatch, helpFile]);

  const mods = useModifiers(styles, 'helpNav', { selected: isSelected });

  if (!helpFile) {
    return (
      <div className={styles.helpNav}>
        <Loader />
      </div>
    );
  }

  return (
    <div data-testid="help-navigation" ref={helpNavRef} className={`${styles.helpNav} ${mods}`}>
      {helpChapters.map((chapter, index) => {
        if (!helpFile[chapter]) return null;
        return (
          <button
            className={`${styles.helpNav__button} ${selectedHelpPage === chapter ? styles.helpNav__button_active : ''}`}
            onClick={(): void => {
              audioTrigger.play({ name: 'menuClick' });
              handleNavigation(chapter);
            }}
            key={index}
          >
            <div
              style={{
                wordBreak: 'break-word',
                paddingRight: '0.75em',
              }}
            >
              <HelpText dynamicValues={{ TABLE_NAME: tableName }}>{String(helpFile[chapter].title)}</HelpText>
            </div>
            <div className={styles.helpNav__icon}>
              <Icon icon={Icons.ChevronDown} />
            </div>
          </button>
        )}
      )}
    </div>
  );
};
